import axiosConfig from "./axiosConfig";

class ReviewersService {
  constructor() {
    this.baseUrl = "/v1/reviewers";
    this.categoriesUrl = "/v1/reviewer-categories";
    this.evaluationManagersUrl = "/v1/evaluation-managers";
  }

  /**
   * Récupère tous les relecteurs avec pagination
   * @param {number} page Page à récupérer
   * @param {number} limit Nombre de relecteurs par page
   * @returns {Promise<Object>} Les données des relecteurs
   */
  async getAll(page, limit) {
    try {
      const response = await axiosConfig.get(
        `${this.baseUrl}?limit=${limit}&page=${page}&order_by=id_rev&order_direction=asc`
      );
      return response.data || { data: [] };
    } catch (error) {
      console.error("Error fetching reviewers:", error);
      return { data: [] };
    }
  }

  /**
   * Récupère tous les relecteurs sans pagination
   * @returns {Promise<Object>} Tous les relecteurs
   */
  async getAllSansPagination(){
    try {
      const response = await axiosConfig.get(
        `${this.baseUrl}?limit=1000&page=1&order_by=id_rev&order_direction=asc`
      );
      return response.data || { data: [] };
    } catch (error) {
      console.error("Error fetching reviewers:", error);
      return { data: [] };
    }
  }

  /**
   * Crée une affectation d'évaluation pour les managers
   * @param {Object} data Données d'évaluation
   * @returns {Promise<Object>} Résultat de l'opération
   */
  async evaluationManagers(data) {
    try {
      const response = await axiosConfig.post(
        this.evaluationManagersUrl,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Erreur lors de l'affectation des relecteurs:", error);
      throw error.response?.data || error.message;
    }
  }

  /**
   * Crée un nouveau relecteur
   * @param {Object} data Données du relecteur
   * @returns {Promise<Object>} Relecteur créé
   */
  async create(data) {
    try {
      const response = await axiosConfig.post(this.baseUrl, data, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error creating reviewer:", error);
      throw error.response?.data || error.message;
    }
  }

  /**
   * Met à jour un relecteur existant
   * @param {number} id ID du relecteur
   * @param {Object} data Nouvelles données
   * @returns {Promise<Object>} Relecteur mis à jour
   */
  async update(id, data) {
    try {
      const response = await axiosConfig.put(`${this.baseUrl}/${id}`, data, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error updating reviewer:", error);
      throw error.response?.data || error.message;
    }
  }

  /**
   * Supprime un relecteur
   * @param {number} id ID du relecteur
   * @returns {Promise<Object>} Résultat de la suppression
   */
  async delete(id) {
    try {
      const response = await axiosConfig.delete(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting reviewer:", error);
      throw error.response?.data || error.message;
    }
  }

  /**
   * Récupère un relecteur par son ID
   * @param {number} id ID du relecteur
   * @returns {Promise<Object>} Le relecteur
   */
  async getById(id) {
    try {
      const response = await axiosConfig.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching reviewer with id ${id}:`, error);
      throw error.response?.data || error.message;
    }
  }

  /**
   * Crée plusieurs relecteurs en parallèle
   * @param {Array<Object>} reviewersData Tableau des données des relecteurs
   * @returns {Promise<Array>} Résultats des créations
   */
  async createMultiple(reviewersData) {
    const promises = reviewersData.map((data) => this.create(data));
    return Promise.all(promises);
  }

  /**
   * Récupère toutes les catégories de relecteurs
   * @param {number} page Page à récupérer
   * @param {number} limit Nombre de catégories par page
   * @param {string} search Terme de recherche
   * @param {string} orderBy Champ de tri
   * @returns {Promise<Object>} Les catégories de relecteurs
   */
  async getAllReviewerCategories(page, limit, search = "", orderBy = "name_cat_rev") {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        page: page.toString(),
        search,
        order_by: orderBy,
        order_direction: "asc"
      });

      const response = await axiosConfig.get(`${this.categoriesUrl}?${params}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching reviewer categories:", error);
      throw error.response?.data || error.message;
    }
  }

  /**
   * Récupère toutes les catégories sans pagination
   * @returns {Promise<Object>} Toutes les catégories
   */
  async getAllReviewerCategoriesSansPagination() {
    try {
      const response = await axiosConfig.get(
        `${this.categoriesUrl}?limit=10&page=1&order_by=name_cat_rev&order_direction=asc`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching all reviewer categories:", error);
      throw error.response?.data || error.message;
    }
  }
}

const reviewersService = new ReviewersService();
export default reviewersService;